import { useCallback, useEffect, useState } from 'react';
import { CanceledError } from 'axios';
import { getApiErrorMessage } from '../../helpers/apiError';
import {
	REMOTE_SUPPORT_LOAD_ERROR_MESSAGE,
	REMOTE_SUPPORT_STATUS,
	isRemoteSupportStatus
} from '../../helpers/remoteSupport';
import { getProductRemoteSupport } from '../../services/remoteSupport';

const loadingStateFor = (productId) => {
	return { productId, status: REMOTE_SUPPORT_STATUS.LOADING, remoteSupport: null, error: null };
};

/*
 * Reads whether a machine's support tunnel can be opened (`GET /v1/licensing/products/{id}/remote-support`)
 * and says what came back: `status` is LOADING, FAILED or READY, and `remoteSupport` is the server's
 * answer when READY. The answer is per card and nothing else reads it, so it stays out of redux.
 *
 * Every result remembers the product it belongs to, and a result for another product is read as
 * LOADING, so no render pairs a newly selected machine with the previous one's status. Unmounting or
 * switching product aborts the request, and an aborted request never lands. `reload` asks again,
 * which the card does once an override is saved: the server computes the entitlement, the card
 * never assumes it.
 */
export const useRemoteSupportStatus = (productId) => {
	const [state, setState] = useState(() => {
		return loadingStateFor(productId);
	});
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!productId) {
			return undefined;
		}

		const controller = new AbortController();

		const loadStatus = async () => {
			try {
				const response = await getProductRemoteSupport(productId, { signal: controller.signal });

				if (controller.signal.aborted) {
					return;
				}

				const remoteSupport = response?.data;

				if (!isRemoteSupportStatus(remoteSupport)) {
					setState({ productId, status: REMOTE_SUPPORT_STATUS.FAILED, remoteSupport: null, error: REMOTE_SUPPORT_LOAD_ERROR_MESSAGE });
					return;
				}

				setState({ productId, status: REMOTE_SUPPORT_STATUS.READY, remoteSupport, error: null });
			} catch (error) {
				if (controller.signal.aborted || error instanceof CanceledError || error?.name === 'AbortError') {
					return;
				}

				setState({
					productId,
					status: REMOTE_SUPPORT_STATUS.FAILED,
					remoteSupport: null,
					error: getApiErrorMessage(error, REMOTE_SUPPORT_LOAD_ERROR_MESSAGE)
				});
			}
		};

		loadStatus();

		return () => {
			controller.abort();
		};
	}, [productId, attempt]);

	const reload = useCallback(() => {
		setState(loadingStateFor(productId));
		setAttempt((currentAttempt) => {
			return currentAttempt + 1;
		});
	}, [productId]);

	const { status, remoteSupport, error } = state.productId === productId ? state : loadingStateFor(productId);

	return { status, remoteSupport, error, reload };
};

export default useRemoteSupportStatus;
